import {
  useMemo,
  useState,
  Reducer,
  useReducer,
  useCallback,
  useEffect,
} from "react";
import { useType } from "./use-type";

const validType = (type?: string) => {
  if (!type) {
    return new Error("不正なパラメータです。");
  }

  return null;
};

type ShindanSchema = {
  type: string;
  display_type: string;
  display_type_sub: string;
  result_priority: number[];
  questions: {
    category: string;
    number: number;
    question: string;
    answers: {
      text: string;
      points: number[];
    }[];
  }[];
  results: string[];
};

type State = {
  totalPoints: number[];
  currentIndex: number;
  progressCount: number;
  totalCount: number;
  isFinished: boolean;
  result?: number[];
  data?: ShindanSchema;
};

type Action =
  | { type: "next" }
  | { type: "addPoint"; payload: { points: number[] } }
  | { type: "aggregate" }
  | { type: "load"; payload: { data: ShindanSchema } };

const initailState: State = {
  totalPoints: new Array(4).fill(0),
  currentIndex: 0,
  progressCount: 0,
  totalCount: 0,
  isFinished: false,
};

const reducer: Reducer<State, Action> = (state, action) => {
  switch (action.type) {
    case "next":
      return {
        ...state,
        currentIndex: state.currentIndex + 1,
        progressCount: state.progressCount - 1,
        isFinished: state.progressCount - 1 <= 0,
      };

    case "addPoint":
      const nextTotalPoints = [...state.totalPoints];
      for (const i of action.payload.points) {
        nextTotalPoints[i] += 1;
      }
      return {
        ...state,
        totalPoints: nextTotalPoints,
      };

    case "aggregate":
      const numbers = state.totalPoints;
      const max = Math.max(...numbers);

      const indexes: number[] = [];
      let index = numbers.indexOf(max);

      while (index !== -1) {
        indexes.push(index);
        index = numbers.indexOf(max, index + 1);
      }

      return {
        ...state,
        result: indexes,
      };

    case "load":
      const data = action.payload.data;
      return {
        ...state,
        totalCount: data.questions.length ?? 0,
        progressCount: data.questions.length ?? 0,
        data: action.payload.data,
      };

    default:
      throw new Error("invalid action type");
  }
};

export const useShindan = () => {
  const type = useType();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(validType(type));
  const [state, dispath] = useReducer(reducer, initailState);

  const handleNext = useCallback(
    (points: number[]) => {
      // const alpha = ["A", "B", "C", "D"];
      // console.log(alpha[points[0]], alpha[points[1]]);

      dispath({ type: "addPoint", payload: { points } });
      if (!state.isFinished) {
        dispath({ type: "next" });
      }
    },
    [dispath, state]
  );

  const result = useMemo(() => {
    if (
      state.result &&
      state.result.length > 0 &&
      state.data?.result_priority &&
      state.data?.results
    ) {
      for (const i of state.data.result_priority) {
        const resultIndex = state.result.find((r) => r === i);
        if (resultIndex !== undefined) {
          return state.data.results[resultIndex];
        }
      }
    }
  }, [state.result, state.data?.result_priority, state.data?.results]);

  useEffect(() => {
    if (state.isFinished) {
      dispath({ type: "aggregate" });
    }
  }, [state.isFinished]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      fetch("./data/shindan.json")
        .then((res) => res.json())
        .then((data) => {
          const found = data.find((d: ShindanSchema) => d.type === type);
          if (found) {
            dispath({ type: "load", payload: { data: found } });
          } else {
            throw new Error("データが取得出来ませんでした。");
          }
        })
        .catch((error) => setError(error))
        .finally(() => setIsLoading(false));
    };

    if (!error) {
      fetchData();
    }
  }, [type, error]);

  return {
    data: state.data,
    title: state.data?.display_type,
    subTitle: state.data?.display_type_sub,
    question: state.data?.questions[state.currentIndex],
    isFinished: state.isFinished,
    progressCount: state.progressCount,
    totalCount: state.totalCount,
    totalPoints: state.totalPoints,
    currentIndex: state.currentIndex,
    result,
    isLoading,
    error,
    type,
    handleNext,
  };
};
